/**
 * Aspect — GizmoControls (manual move / rotate / swap)
 * --------------------------------------------------------------
 * While an item is selected the player cannot walk (see FirstPersonControls).
 * The piece follows the cursor continuously: in walk mode the crosshair floor
 * ray drives position; outside pointer-lock the mouse position does. Q/E apply
 * continuous fluid rotation. Left-click deselects; right-click reverts (Picker).
 */

import * as pc from "playcanvas";
import { applyPlacementTransform, normalizeYaw } from "../scene/transforms.js";
import { QERotationInput } from "./rotation_keys.js";
import { resolveFurnitureXZ, isWallMounted, wallMountPoseFromRay } from "../scene/furniture_placement.js";
import {
  childrenOf,
  clampToSupportXZ,
  findSupportAt,
  isSurfaceItem,
  supportTopY,
} from "../scene/surface_stack.js";

const SEND_THROTTLE_MS = 80;
const MOVE_SMOOTH = 10;
const ROT_SMOOTH = 14;
/** Floor-aim hits farther than this (m) are clamped — near-horizon rays jitter. */
const AIM_MAX_DIST = 6;
const AIM_MIN_DIST = 0.45;
// Keep furniture this far OFF the wall faces so it never looks stuck/embedded.
const WALL_PADDING = 0.08;
// Default wall half-thickness (the serialized room omits thickness on the client).
const WALL_HALF_THICK = 0.075;
// Doorways are widened by this so a piece can still be dragged room-to-room.
const DOOR_SLACK = 0.25;

function extentsXZ(fp, rotDeg, scale) {
  const r = (rotDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const s = Math.abs(Math.sin(r));
  const hw = (fp.w / 2) * scale;
  const hd = (fp.d / 2) * scale;
  return { ex: c * hw + s * hd, ez: s * hw + c * hd };
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Closest point on segment AB to P (returns the point + param t in [0,1]). */
function closestOnSegment(ax, az, bx, bz, px, pz) {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  if (len2 < 1e-9) return { x: ax, z: az };
  const t = clamp(((px - ax) * abx + (pz - az) * abz) / len2, 0, 1);
  return { x: ax + t * abx, z: az + t * abz };
}

function lerpAngle(from, to, t) {
  from = normalizeYaw(from);
  to = normalizeYaw(to);
  let diff = to - from;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return normalizeYaw(from + diff * t);
}

function expSmooth(current, target, speed, dt) {
  const k = 1 - Math.exp(-speed * dt);
  return current + (target - current) * k;
}

export class GizmoControls {
  constructor(app, camera, sceneManager, net, picker) {
    this.app = app;
    this.camera = camera;
    this.scene = sceneManager;
    this.net = net;
    this.picker = picker;
    this.canvas = app.graphicsDevice?.canvas ?? null;

    this._lastSent = 0;
    this._lastMouseX = 0;
    this._lastMouseY = 0;

    this._targetX = 0;
    this._targetZ = 0;
    this._targetY = 0;
    this._targetParentId = null;
    this._smoothX = 0;
    this._smoothZ = 0;
    this._targetRotY = 0;
    this._smoothRotY = 0;
    this._baseline = null;
    this._grabbed = false;
    this._grabOffX = 0;
    this._grabOffZ = 0;

    this._wallSegCache = null;
    this._wallSegRoom = null;

    this._qeRotate = new QERotationInput({
      getYaw: () => (this._group ? this._group.yaw : this._targetRotY),
      setYaw: (y) => {
        if (this._group) this._group.yaw = y;
        else this._targetRotY = y;
      },
    });

    /** Marquee group being moved: { ids, baselines: Map, anchor, dx, dz } */
    this._group = null;
    /** Items resting on the selected piece (no parent link) that ride along. */
    this._riders = [];

    this._bindInput();
    this.app.on("update", (dt) => this._update(dt));
    this.app.on("scene:selected", ({ id }) => this._onSelected(id));
    this.app.on("ui:edit-cancel", ({ id }) => this._revertEdit(id));
    this.app.on("ui:swap-asset", ({ asset_id }) => this._swapSelected(asset_id));
    this.app.on("scene:multi-selected", ({ ids }) => this._onMultiSelected(ids ?? []));
    this.app.on("ui:commit-multi", () => this._commitGroup());
    this.app.on("ui:edit-cancel-multi", () => this._revertGroup());
  }

  get selectedId() {
    return this.picker.selectedId;
  }

  /** @deprecated kept for headless tests that set dragging=true */
  get dragging() {
    return Boolean(this.selectedId);
  }

  set dragging(_v) {
    /* no-op: movement is always cursor-driven while selected */
  }

  _onSelected(id) {
    this._qeRotate.reset();
    if (!id) {
      this._baseline = null;
      this._riders = [];
      this._grabbed = false;
      if (!this.picker.selectedIds?.size) this._group = null;
      this.scene.syncWalkObstacles();
      return;
    }
    // Marquee selection fires scene:selected with the first id for UI/walk
    // compat — the group branch owns movement then, not the single path.
    if (this.picker.selectedIds?.size) return;
    const rec = this.scene.placements.get(id);
    if (!rec) return;
    this._baseline = {
      id,
      position: [...rec.position],
      rotation_y: normalizeYaw(rec.rotation_y ?? 0),
      parent_id: rec.parent_id ?? null,
    };
    this._targetX = rec.position[0];
    this._targetZ = rec.position[2];
    this._targetY = rec.position[1];
    this._targetParentId = rec.parent_id ?? null;
    this._smoothX = this._targetX;
    this._smoothZ = this._targetZ;
    this._targetRotY = this._baseline.rotation_y;
    this._smoothRotY = this._targetRotY;
    this._grabbed = false;
    this._grabOffX = 0;
    this._grabOffZ = 0;
    this._collectRiders(rec);
  }

  /**
   * Items physically resting on/inside the selected piece (books on a shelf,
   * dishes on a counter) that have no parent link ride along with the move.
   * Wall/ceiling fixtures never ride; parent-linked children cascade already.
   */
  _collectRiders(rec) {
    this._riders = [];
    const meta = this.scene.catalog?.get?.(rec.asset_id);
    // Only floor-standing pieces carry riders (not wall art / ceiling lamps).
    if (meta?.placement_mode) return;
    const fp = rec.footprint ?? meta?.footprint;
    if (!fp) return;
    const scale = rec.scale ?? 1;
    const { ex, ez } = extentsXZ(fp, rec.rotation_y ?? 0, scale);
    const topY = rec.position[1] + (fp.h ?? 1) * scale + 0.6;
    const baseY = rec.position[1] + 0.02;

    for (const p of this.scene.placements?.values?.() ?? []) {
      if (p.id === rec.id || p.parent_id === rec.id) continue;
      const pMeta = this.scene.catalog?.get?.(p.asset_id);
      const mode = pMeta?.placement_mode ?? null;
      if (mode === "wall" || mode === "ceiling") continue;
      if (p.position[1] <= baseY || p.position[1] > topY) continue;
      if (Math.abs(p.position[0] - rec.position[0]) > ex - 0.02) continue;
      if (Math.abs(p.position[2] - rec.position[2]) > ez - 0.02) continue;
      this._riders.push({ id: p.id, baseline: [...p.position] });
    }
  }

  /** Translate riders by the same world delta as the moved support. */
  _moveRiders(dx, dz) {
    if (!this._riders.length || (Math.abs(dx) < 1e-6 && Math.abs(dz) < 1e-6)) return;
    for (const r of this._riders) {
      const rec = this.scene.placements.get(r.id);
      const ent = this.scene.entities.get(r.id);
      if (!rec || !ent) continue;
      rec.position = [rec.position[0] + dx, rec.position[1], rec.position[2] + dz];
      applyPlacementTransform(ent, rec);
      this._throttledSend(rec);
    }
  }

  /** Restore the item to its pose when selection started (right-click cancel). */
  _revertEdit(id) {
    if (!id || !this._baseline || this._baseline.id !== id) return;
    const rec = this.scene.placements.get(id);
    const entity = this.scene.entities.get(id);
    if (!rec || !entity) return;

    // Riders return to their pre-drag spots too.
    for (const r of this._riders) {
      const rRec = this.scene.placements.get(r.id);
      const rEnt = this.scene.entities.get(r.id);
      if (!rRec || !rEnt) continue;
      rRec.position = [...r.baseline];
      applyPlacementTransform(rEnt, rRec);
      this._sendNow(rRec);
    }
    rec.position = [...this._baseline.position];
    rec.rotation_y = this._baseline.rotation_y;
    if (this._baseline.parent_id) rec.parent_id = this._baseline.parent_id;
    else delete rec.parent_id;
    this._targetX = rec.position[0];
    this._targetZ = rec.position[2];
    this._targetY = rec.position[1];
    this._targetParentId = this._baseline.parent_id;
    this._smoothX = this._targetX;
    this._smoothZ = this._targetZ;
    this._targetRotY = this._baseline.rotation_y;
    this._smoothRotY = this._targetRotY;
    applyPlacementTransform(entity, rec);
    this.scene.syncWalkObstacles();
    this._sendNow(rec);
    this._baseline = null;
  }

  _bindInput() {
    const m = this.app.mouse;
    if (m) {
      m.on(pc.EVENT_MOUSEMOVE, (e) => {
        this._lastMouseX = e.x;
        this._lastMouseY = e.y;
      });
    }
    const t = this.app.touch;
    if (t) {
      t.on(pc.EVENT_TOUCHMOVE, (e) => {
        const touch = e.touches[0];
        if (touch) {
          this._lastMouseX = touch.x;
          this._lastMouseY = touch.y;
        }
      });
    }
    const kb = this.app.keyboard;
    if (kb) {
      kb.on(pc.EVENT_KEYDOWN, (e) => {
        if ((!this.selectedId && !this._group) || document.body.classList.contains("plan-editor-open")) return;
        if (e.key === pc.KEY_Q) this._qeRotate.onKeyDown("q", e.event?.repeat);
        else if (e.key === pc.KEY_E) this._qeRotate.onKeyDown("e", e.event?.repeat);
      });
      kb.on(pc.EVENT_KEYUP, (e) => {
        if ((!this.selectedId && !this._group) || document.body.classList.contains("plan-editor-open")) return;
        if (e.key === pc.KEY_Q) this._qeRotate.onKeyUp("q");
        else if (e.key === pc.KEY_E) this._qeRotate.onKeyUp("e");
      });
    }
  }

  _pointerLocked() {
    return this.canvas && document.pointerLockElement === this.canvas;
  }

  /** Screen point that drives the selected item (crosshair when pointer-locked). */
  _cursorScreenCoords() {
    if (this._pointerLocked()) {
      return { x: this.canvas.clientWidth / 2, y: this.canvas.clientHeight / 2 };
    }
    return { x: this._lastMouseX, y: this._lastMouseY };
  }

  _pointerOnFloor(x, y) {
    const cam = this.camera.camera;
    const near = cam.screenToWorld(x, y, cam.nearClip);
    const far = cam.screenToWorld(x, y, cam.farClip);
    const dir = far.clone().sub(near).normalize();
    const floorY = this.scene.room?.floorY ?? 0;
    if (Math.abs(dir.y) < 1e-6) return null;
    const t = (floorY - near.y) / dir.y;
    if (t < 0) return null;
    return new pc.Vec3(near.x + dir.x * t, floorY, near.z + dir.z * t);
  }

  /** Keep a floor aim a usable distance in front of the camera (kills horizon flicker). */
  _clampAim(hit, pos, floorY) {
    const dx = hit.x - pos.x;
    const dz = hit.z - pos.z;
    const dist = Math.hypot(dx, dz);
    if (dist < AIM_MIN_DIST) {
      const nx = dist > 1e-4 ? dx / dist : this.camera.forward.x;
      const nz = dist > 1e-4 ? dz / dist : this.camera.forward.z;
      const flat = Math.hypot(nx, nz) || 1;
      return new pc.Vec3(pos.x + (nx / flat) * AIM_MIN_DIST, floorY, pos.z + (nz / flat) * AIM_MIN_DIST);
    }
    if (dist > AIM_MAX_DIST) {
      const s = AIM_MAX_DIST / dist;
      return new pc.Vec3(pos.x + dx * s, floorY, pos.z + dz * s);
    }
    return hit;
  }

  /** Floor point under the aim — screen ray, then look-forward fallback (walls / horizon). */
  _aimOnFloor(x, y) {
    const floorY = this.scene.room?.floorY ?? 0;
    const pos = this.camera.getPosition();
    const hit = this._pointerOnFloor(x, y);
    if (hit) return this._clampAim(hit, pos, floorY);
    const fwd = this.camera.forward;
    const flat = Math.hypot(fwd.x, fwd.z);
    if (Math.abs(fwd.y) > 1e-4) {
      const t = (floorY - pos.y) / fwd.y;
      if (t > 0.35 && t < 40) {
        return this._clampAim(new pc.Vec3(pos.x + fwd.x * t, floorY, pos.z + fwd.z * t), pos, floorY);
      }
    }
    const nx = flat > 1e-4 ? fwd.x / flat : 0;
    const nz = flat > 1e-4 ? fwd.z / flat : 1;
    return new pc.Vec3(pos.x + nx * 2.6, floorY, pos.z + nz * 2.6);
  }

  /**
   * Solid wall sub-segments for the current room, with door openings cut out so
   * pieces can still be dragged through doorways. Cached per loaded room.
   */
  _wallSegments() {
    const room = this.scene.room;
    if (!room?.walls) return [];
    if (this._wallSegRoom === room && this._wallSegCache) return this._wallSegCache;

    const openings = room.openings ?? [];
    const segs = [];
    for (const w of room.walls) {
      const ax = w.start[0];
      const az = w.start[1];
      const bx = w.end[0];
      const bz = w.end[1];
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 1e-4) continue;
      const half = (w.thickness ?? WALL_HALF_THICK * 2) / 2;

      // Door gaps along this wall (as [t0,t1] spans), widened by DOOR_SLACK.
      const gaps = [];
      for (const op of openings) {
        if (op.wall !== w.id || op.type !== "door") continue;
        const tc = ((op.center[0] - ax) * (bx - ax) + (op.center[2] - az) * (bz - az)) / (len * len);
        const hw = ((op.width ?? 0.9) / 2 + DOOR_SLACK) / len;
        gaps.push([clamp(tc - hw, 0, 1), clamp(tc + hw, 0, 1)]);
      }
      gaps.sort((a, b) => a[0] - b[0]);

      const dirx = (bx - ax) / len;
      const dirz = (bz - az) / len;
      const add = (t0, t1) => {
        if (t1 - t0 < 0.02) return;
        segs.push({
          ax: ax + dirx * len * t0,
          az: az + dirz * len * t0,
          bx: ax + dirx * len * t1,
          bz: az + dirz * len * t1,
          half,
          nx: -dirz,
          nz: dirx,
        });
      };
      let cursor = 0;
      for (const [g0, g1] of gaps) {
        if (g0 > cursor) add(cursor, g0);
        cursor = Math.max(cursor, g1);
      }
      if (cursor < 1) add(cursor, 1);
    }

    this._wallSegRoom = room;
    this._wallSegCache = segs;
    return segs;
  }

  /**
   * Push a footprint center (px,pz) out of every wall so the piece never crosses
   * a wall and always keeps WALL_PADDING clear of the faces. The clearance is
   * direction-aware (uses the footprint extent along the push axis) so a piece
   * can still sit close to a wall on its short side.
   */
  _resolveAgainstWalls(px, pz, rec) {
    const segs = this._wallSegments();
    if (!segs.length) return { x: px, z: pz };
    const { ex, ez } = extentsXZ(rec.footprint, this._targetRotY, rec.scale ?? 1);
    let x = px;
    let z = pz;

    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const s of segs) {
        const cp = closestOnSegment(s.ax, s.az, s.bx, s.bz, x, z);
        let dx = x - cp.x;
        let dz = z - cp.z;
        let dist = Math.hypot(dx, dz);
        let nx;
        let nz;
        if (dist < 1e-6) {
          // Center sits on the wall line — push along the wall's own normal.
          nx = s.nx;
          nz = s.nz;
          dist = 0;
        } else {
          nx = dx / dist;
          nz = dz / dist;
        }
        const reach = s.half + (ex * Math.abs(nx) + ez * Math.abs(nz)) + WALL_PADDING;
        if (dist < reach - 1e-4) {
          x = cp.x + nx * reach;
          z = cp.z + nz * reach;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return { x, z };
  }

  /** World-space cursor ray. */
  _cursorRay(x, y) {
    const cam = this.camera.camera;
    const near = cam.screenToWorld(x, y, cam.nearClip);
    const far = cam.screenToWorld(x, y, cam.farClip);
    const dir = far.clone().sub(near).normalize();
    return { origin: near, dir };
  }

  _setTargetFromScreen(x, y, rec) {
    const catalogMeta = this.scene.catalog?.get?.(rec.asset_id);

    // Wall-hung items (paintings, TV, mirror…) ride the wall under the cursor:
    // flush against the face (mesh-exact, no padding), sliding in X/Z *and* Y,
    // auto-facing away from the wall. No obstacle/wall-padding resolution.
    if (isWallMounted(catalogMeta) || isWallMounted(rec)) {
      const { origin, dir } = this._cursorRay(x, y);
      const pose = wallMountPoseFromRay(origin, dir, this.scene.room, rec.footprint, rec.scale ?? 1, rec.asset_id);
      if (!pose) return false; // no wall under the cursor — hold the last pose
      this._targetX = pose.x;
      this._targetZ = pose.z;
      this._targetY = pose.y;
      this._targetRotY = pose.rotation_y;
      this._targetParentId = null;
      return true;
    }

    const hit = this._aimOnFloor(x, y);
    if (!hit) return false;

    // Grab offset: the piece follows the cursor delta instead of snapping to
    // the floor hit (and then fighting neighbors every frame).
    if (!this._grabbed) {
      this._grabOffX = rec.position[0] - hit.x;
      this._grabOffZ = rec.position[2] - hit.z;
      this._grabbed = true;
    }

    const placements = [...(this.scene.placements?.values?.() ?? [])];
    const catalogLookup = (assetId) => this.scene.catalog?.get?.(assetId);
    const childIds = childrenOf(rec.id, placements).map((c) => c.id);
    const exclude = new Set([rec.id, ...childIds, ...(this._riders ?? []).map((r) => r.id)]);
    if (rec.parent_id) exclude.add(rec.parent_id);

    let wx = hit.x + this._grabOffX;
    let wz = hit.z + this._grabOffZ;
    let support = null;

    const meta = catalogLookup(rec.asset_id) ?? rec;
    const surfaceCapable = isSurfaceItem(meta) || isSurfaceItem(rec) || Boolean(rec.parent_id);

    if (surfaceCapable) {
      support = findSupportAt(wx, wz, placements, { excludeId: rec.id, catalogLookup });
      if (support) {
        const clamped = clampToSupportXZ(wx, wz, rec.footprint, this._targetRotY, rec.scale ?? 1, support);
        wx = clamped.x;
        wz = clamped.z;
        exclude.add(support.id);
      }
    }

    const resolved = resolveFurnitureXZ(
      wx,
      wz,
      {
        footprint: rec.footprint,
        rotation_y: this._targetRotY,
        scale: rec.scale ?? 1,
        excludeId: exclude,
        // Stable last target — never the smoothed mesh, or the wall solver
        // flips sides as the visual lags behind the cursor.
        prev: { x: this._targetX, z: this._targetZ },
      },
      this.scene.room,
      // Drag: walls/bounds only. Neighbor shove is what made couches jitter.
      null,
    );

    this._targetX = resolved.x;
    this._targetZ = resolved.z;

    if (surfaceCapable) {
      // Re-evaluate support after obstacle resolve (XZ may have shifted).
      support = findSupportAt(this._targetX, this._targetZ, placements, {
        excludeId: rec.id,
        catalogLookup,
      });
      if (support) {
        const clamped = clampToSupportXZ(
          this._targetX,
          this._targetZ,
          rec.footprint,
          this._targetRotY,
          rec.scale ?? 1,
          support,
        );
        this._targetX = clamped.x;
        this._targetZ = clamped.z;
        this._targetY = supportTopY(support);
        this._targetParentId = support.id;
      } else {
        this._targetY = this.scene.room?.floorY ?? 0;
        this._targetParentId = null;
      }
    } else {
      this._targetY = rec.position[1];
      this._targetParentId = rec.parent_id ?? null;
    }

    return true;
  }

  /** Direct snap (headless tests). */
  _dragTo(x, y) {
    const id = this.selectedId;
    const rec = this.scene.placements.get(id);
    const entity = this.scene.entities.get(id);
    if (!rec || !entity) return;
    this._grabbed = true;
    this._grabOffX = 0;
    this._grabOffZ = 0;
    if (!this._setTargetFromScreen(x, y, rec)) return;

    this._smoothX = this._targetX;
    this._smoothZ = this._targetZ;
    const prev = [...rec.position];
    rec.position = [this._smoothX, this._targetY ?? rec.position[1], this._smoothZ];
    if (this._targetParentId) rec.parent_id = this._targetParentId;
    else delete rec.parent_id;
    this._applyVisual(entity, rec);
    this._cascadeChildren(rec, prev);
    this._moveRiders(rec.position[0] - prev[0], rec.position[2] - prev[2]);
    this._sendNow(rec);
  }

  _endDrag() {
    const rec = this.scene.placements.get(this.selectedId);
    if (rec) {
      this._sendNow(rec);
      for (const child of childrenOf(rec.id, this.scene.placements?.values?.() ?? [])) {
        this._sendNow(child);
      }
      for (const r of this._riders) {
        const rRec = this.scene.placements.get(r.id);
        if (rRec) this._sendNow(rRec);
      }
    }
    this.scene.syncWalkObstacles();
  }

  /** Flush the selected item's live pose into the placement record (for Admin Save). */
  flushPending() {
    const id = this.selectedId;
    if (!id) return;
    const rec = this.scene.placements.get(id);
    const entity = this.scene.entities.get(id);
    if (!rec || !entity) return;
    if (Number.isFinite(this._smoothX) && Number.isFinite(this._smoothZ)) {
      rec.position = [this._smoothX, this._targetY ?? rec.position[1], this._smoothZ];
    }
    if (Number.isFinite(this._smoothRotY)) {
      rec.rotation_y = normalizeYaw(this._smoothRotY);
    }
    if (this._targetParentId) rec.parent_id = this._targetParentId;
    applyPlacementTransform(entity, { ...rec, rotation_y: rec.rotation_y });
    this._endDrag();
  }

  _applyVisual(entity, rec) {
    applyPlacementTransform(entity, { ...rec, rotation_y: this._smoothRotY });
  }

  /** Move stacked children by the same world Δ as the parent. */
  _cascadeChildren(parentRec, prevPos) {
    if (!parentRec?.id || !prevPos) return;
    const dx = parentRec.position[0] - prevPos[0];
    const dz = parentRec.position[2] - prevPos[2];
    if (Math.abs(dx) < 1e-6 && Math.abs(dz) < 1e-6) return;

    for (const child of childrenOf(parentRec.id, this.scene.placements?.values?.() ?? [])) {
      const cPrev = [...child.position];
      child.position = [child.position[0] + dx, child.position[1], child.position[2] + dz];
      const ent = this.scene.entities.get(child.id);
      if (ent) applyPlacementTransform(ent, child);
      this._throttledSend(child);
      // Nested stacks (rare): recurse
      this._cascadeChildren(child, cPrev);
    }
  }

  // ---------------------------------------------------------------- group move
  _onMultiSelected(ids) {
    this._qeRotate.reset();
    if (!ids.length) {
      this._group = null;
      return;
    }
    const baselines = new Map();
    let cx = 0;
    let cz = 0;
    for (const id of ids) {
      const rec = this.scene.placements.get(id);
      if (!rec) continue;
      baselines.set(id, {
        position: [...rec.position],
        rotation_y: normalizeYaw(rec.rotation_y ?? 0),
      });
      cx += rec.position[0];
      cz += rec.position[2];
    }
    if (!baselines.size) {
      this._group = null;
      return;
    }
    this._group = {
      ids: [...baselines.keys()],
      baselines,
      anchor: [cx / baselines.size, cz / baselines.size],
      dx: 0,
      dz: 0,
      sdx: 0,
      sdz: 0,
      yaw: 0,
      syaw: 0,
      grabbed: false,
    };
  }

  /** Cursor-driven group translation (+ Q/E rotation about the group center).
   *  Every item keeps its own height — only X/Z (and yaw) change. */
  _updateGroup(dt) {
    const g = this._group;
    if (this._qeRotate.needsTick()) this._qeRotate.tick(dt);

    const { x, y } = this._cursorScreenCoords();
    const hit = this._aimOnFloor(x, y);
    if (hit) {
      // First cursor sample becomes the neutral grab point so the group
      // doesn't jump to the cursor on selection.
      if (!g.grabbed) {
        g.grabX = hit.x;
        g.grabZ = hit.z;
        g.grabbed = true;
      }
      g.dx = hit.x - g.grabX;
      g.dz = hit.z - g.grabZ;
    }

    g.sdx = expSmooth(g.sdx, g.dx, MOVE_SMOOTH, dt);
    g.sdz = expSmooth(g.sdz, g.dz, MOVE_SMOOTH, dt);
    g.syaw = lerpAngle(g.syaw, g.yaw, 1 - Math.exp(-ROT_SMOOTH * dt));

    const rad = (g.syaw * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    for (const id of g.ids) {
      const rec = this.scene.placements.get(id);
      const ent = this.scene.entities.get(id);
      if (!rec || !ent) continue;
      const b = g.baselines.get(id);
      const ox = b.position[0] - g.anchor[0];
      const oz = b.position[2] - g.anchor[1];
      const rx = ox * c + oz * s;
      const rz = -ox * s + oz * c;
      rec.position = [g.anchor[0] + rx + g.sdx, b.position[1], g.anchor[1] + rz + g.sdz];
      rec.rotation_y = normalizeYaw(b.rotation_y + g.syaw);
      applyPlacementTransform(ent, rec);
    }
    this._throttledSendGroup();
  }

  _throttledSendGroup() {
    const now = performance.now();
    if (now - this._lastSent < SEND_THROTTLE_MS) return;
    this._lastSent = now;
    for (const id of this._group?.ids ?? []) {
      const rec = this.scene.placements.get(id);
      if (rec) this._sendNow(rec);
    }
  }

  /** Left-click drop: keep poses, flush them to the server. */
  _commitGroup() {
    for (const id of this._group?.ids ?? []) {
      const rec = this.scene.placements.get(id);
      if (rec) this._sendNow(rec);
    }
    this._group = null;
    this.scene.syncWalkObstacles();
  }

  /** Right-click / Esc: every item returns to its pre-drag pose. */
  _revertGroup() {
    for (const id of this._group?.ids ?? []) {
      const rec = this.scene.placements.get(id);
      const ent = this.scene.entities.get(id);
      const b = this._group.baselines.get(id);
      if (!rec || !ent || !b) continue;
      rec.position = [...b.position];
      rec.rotation_y = b.rotation_y;
      applyPlacementTransform(ent, rec);
      this._sendNow(rec);
    }
    this._group = null;
    this.scene.syncWalkObstacles();
  }

  _update(dt) {
    if (this._group) {
      this._updateGroup(dt);
      return;
    }
    const id = this.selectedId;
    if (!id) return;

    const rec = this.scene.placements.get(id);
    const entity = this.scene.entities.get(id);
    if (!rec || !entity) return;

    if (!document.body.classList.contains("plan-editor-open") && this._qeRotate.needsTick()) {
      this._qeRotate.tick(dt);
    }

    const { x, y } = this._cursorScreenCoords();
    this._setTargetFromScreen(x, y, rec);

    const prevX = this._smoothX;
    const prevZ = this._smoothZ;
    const prevR = this._smoothRotY;
    const prevPos = [...rec.position];

    this._smoothX = expSmooth(this._smoothX, this._targetX, MOVE_SMOOTH, dt);
    this._smoothZ = expSmooth(this._smoothZ, this._targetZ, MOVE_SMOOTH, dt);
    this._smoothRotY = lerpAngle(this._smoothRotY, this._targetRotY, 1 - Math.exp(-ROT_SMOOTH * dt));

    const moved =
      Math.abs(this._smoothX - prevX) > 1e-4 ||
      Math.abs(this._smoothZ - prevZ) > 1e-4 ||
      Math.abs(normalizeYaw(this._smoothRotY - prevR)) > 0.05 ||
      Math.abs((this._targetY ?? rec.position[1]) - rec.position[1]) > 1e-4;

    if (!moved) return;

    const nextY = this._targetY ?? rec.position[1];
    rec.position = [this._smoothX, nextY, this._smoothZ];
    rec.rotation_y = normalizeYaw(this._smoothRotY);
    if (this._targetParentId) rec.parent_id = this._targetParentId;
    else if (isSurfaceItem(this.scene.catalog?.get?.(rec.asset_id) ?? rec) || isSurfaceItem(rec)) {
      delete rec.parent_id;
    }

    this._applyVisual(entity, rec);
    this._cascadeChildren(rec, prevPos);
    this._moveRiders(rec.position[0] - prevPos[0], rec.position[2] - prevPos[2]);
    this._throttledSend(rec);
  }

  async _swapSelected(asset_id) {
    const id = this.selectedId;
    if (!id || !asset_id) return;
    await this.scene.swap(id, asset_id);
    this._onSelected(id);
    this.picker.refresh();
  }

  _throttledSend(rec) {
    const now = performance.now();
    if (now - this._lastSent < SEND_THROTTLE_MS) return;
    this._lastSent = now;
    this._sendNow(rec);
  }

  _sendNow(rec) {
    if (this.scene?.sandbox) return;
    this.net.notify("viewport:transform_changed", {
      id: rec.id,
      position: rec.position,
      rotation_y: normalizeYaw(rec.rotation_y ?? 0),
      scale: rec.scale ?? 1,
      parent_id: rec.parent_id ?? null,
    });
  }
}

export default GizmoControls;
